/**
 * Channels API
 * Manages channel integrations for agents (Slack, Teams, Web)
 */

import { BaseApiClient } from "./base";

// ========== Types ==========

export type ChannelStatus = "pending_oauth" | "active" | "error";

export interface Channel {
  id: string;
  workspace_id: string;
  platform: string;
  app_id: string;
  name: string;
  status: ChannelStatus;
  config?: Record<string, any>;
  agent_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ChannelTemplate {
  app_id: string;
  platform: string;
  name: string;
  description?: string | null;
}

export interface CreateChannelRequest {
  platform: string;
  app_id: string;
  name: string;
  config?: Record<string, any>;
}

export interface UpdateChannelRequest {
  config?: Record<string, any>;
}

// ========== API Client ==========

class ChannelsApiClient extends BaseApiClient {
  /**
   * List all channels for the workspace
   */
  async list(): Promise<Channel[]> {
    return this.request<Channel[]>("/channels");
  }

  /**
   * List available channel templates
   */
  async listTemplates(): Promise<ChannelTemplate[]> {
    return this.request<ChannelTemplate[]>("/channels/templates");
  }

  /**
   * Create a new channel
   */
  async create(data: CreateChannelRequest): Promise<Channel> {
    return this.request<Channel>("/channels", {
      method: "POST",
      body: JSON.stringify(data)
    });
  }

  /**
   * Get a specific channel by ID
   */
  async get(id: string): Promise<Channel> {
    return this.request<Channel>(`/channels/${id}`);
  }

  /**
   * Update a channel
   */
  async update(id: string, data: UpdateChannelRequest): Promise<Channel> {
    return this.request<Channel>(`/channels/${id}`, {
      method: "PUT",
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete a channel
   */
  async delete(id: string): Promise<{ success: boolean; id: string }> {
    return this.request<{ success: boolean; id: string }>(`/channels/${id}`, {
      method: "DELETE"
    });
  }

  /**
   * Initiate OAuth flow for a channel
   * Opens OAuth URL in new tab
   */
  async initiateOAuth(id: string): Promise<void> {
    const headers = await this.getHeaders();
    const url = `${this.baseUrl}/channels/${id}/oauth`;

    const response = await fetch(url, {
      method: "POST",
      headers
    });

    const data = await response.json();
    if (data.url) {
      window.open(data.url, "_blank");
    } else {
      throw new Error("Failed to initiate channel authorization");
    }
  }

  /**
   * Get users from a channel (e.g., Slack users)
   */
  async getChannelUsers(channelId: string): Promise<any[]> {
    return this.request<any[]>(`/channels/${channelId}/users`);
  }
}

export const channelsApi = new ChannelsApiClient();
